import { FunctionComponent, ReactElement, useMemo } from 'react';
import { BaseTextFieldProps, Stack, TextFieldVariants } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CustomDateRange } from '@/shared/types/date';
import { DEFAULT_START_DATE } from '@/shared/utils/date-range';

interface DateRangePickerProps {
	value: CustomDateRange;
	onChange: (dateRange: CustomDateRange) => void;
	variant?: TextFieldVariants;
	size?: BaseTextFieldProps['size'];
}

const DateRangePicker: FunctionComponent<DateRangePickerProps> = ({
	value,
	onChange,
	variant = 'filled',
	size,
}): ReactElement => {
	const isInvalidRange = useMemo(
		() =>
			value.start?.isAfter(value.end) ||
			value.start?.isBefore(DEFAULT_START_DATE, 'year') ||
			value.end?.isBefore(DEFAULT_START_DATE, 'year'),
		[value.start, value.end]
	);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack direction="row" spacing={2}>
				<DatePicker
					label="Start date"
					value={value.start}
					onChange={(start) => onChange({ ...value, start })}
					slotProps={{
						textField: {
							variant,
							size,
							error: isInvalidRange,
						},
					}}
					disableFuture
				/>
				<DatePicker
					label="End date"
					value={value.end}
					onChange={(end) => onChange({ ...value, end })}
					slotProps={{
						textField: {
							variant,
							size,
							error: isInvalidRange,
						},
					}}
					disableFuture
				/>
			</Stack>
		</LocalizationProvider>
	);
};

export default DateRangePicker;
